import {storesApiClient} from "@/configuration/api-client";
import authentication from "@/configuration/authentication";
import { PageName } from "@/configuration/pages";
import { EventBus } from "@/helpers/EventBus";
import { GetStoresResponse } from "@/model/GetStoresResponse";
import { Store } from "@/model/Store";
import { Zone } from "@/model/Zone";
import { Service } from "vue-typedi";
import {CalendarRange} from "@/model/calendar/CalendarRange";

@Service()
export class StoreService {

    private static readonly STORES_ENDPOINT: string = '/stores';
    private static readonly STORE_ID: string = "storeId";
    private static readonly ZONES: string = "zones-";
    private static readonly RANGE: string = "range-";

    private store: Store;

    get currentZones(): Zone[] {
        if (this.store == null) {
            return [];
        }

        if (this.store.zones == null) {
            return [];
        }

        return this.store.zones.filter(zone => authentication.isAuthorizedZone(zone.zoneCode));
    }

    get allZones(): Zone[] {
        if (this.store == null) {
            return [];
        }

        return this.store.zones || [];
    }

    get selectedAndEnabledZones(): string[] {
        const zoneKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID);

        if (localStorage.getItem(zoneKey) == null || JSON.parse(localStorage.getItem(zoneKey)).length == 0) {
            return this.currentZones
                .filter(zone => zone.enabled)
                .map(zone => zone.zoneCode) || [];
        }

        return JSON.parse(localStorage.getItem(zoneKey))
            .filter((zoneCode: string) => this.isZoneEnabled(zoneCode)) || [];
    }

    get isOrderWithDelay(): boolean {
        const withDelayKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID) + "-withDelay";

        return localStorage.getItem(withDelayKey) === "true";
    }

    get isOrderWithMissing(): boolean {
        const withMissingKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID) + "-withMissing";

        return localStorage.getItem(withMissingKey) === "true";
    }

    get isOrderWithCateringService(): boolean {
        const withCateringServiceKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID) + "-withCateringService";

        return localStorage.getItem(withCateringServiceKey) === "true";
    }

    get selectedRange(): CalendarRange {
        const rangeKey: string = StoreService.RANGE + sessionStorage.getItem(StoreService.STORE_ID);
        const storedValue = localStorage.getItem(rangeKey);

        if (storedValue == null) {
            return null;
        }

        return JSON.parse(storedValue);
    }

    get selectedZones(): string {
        const zoneKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID);

        if (localStorage.getItem(zoneKey) == null) {
            return null;
        }

        return JSON.parse(localStorage.getItem(zoneKey))
            .join();
    }

    public saveSelectedZones(zoneCodes: string[]) {
        const zoneKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID);
        localStorage.setItem(zoneKey, JSON.stringify(zoneCodes));

        EventBus.$emit(EventBus.UPDATE_SELECTED_ZONES);
    }

    public saveWithDelayFilter(withDelay: boolean) {
        const withDelayKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID) + "-withDelay";
        localStorage.setItem(withDelayKey, withDelay.toString());

        EventBus.$emit(EventBus.UPDATE_FILTER_WITH_DELAY);
    }

    public saveWithMissingFilter(withMissing: boolean) {
        const withMissingKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID) + "-withMissing";
        localStorage.setItem(withMissingKey, withMissing.toString());

        EventBus.$emit(EventBus.UPDATE_FILTER_WITH_MISSING);
    }

    public saveWithCateringServiceFilter(withMissing: boolean) {
        const withMissingKey: string = StoreService.ZONES + sessionStorage.getItem(StoreService.STORE_ID) + "-withCateringService";
        localStorage.setItem(withMissingKey, withMissing.toString());

        EventBus.$emit(EventBus.UPDATE_FILTER_WITH_CATERING_SERVICE);
    }

    public saveSelectedRange(selectedRange: CalendarRange) {
        const rangeKey: string = StoreService.RANGE + sessionStorage.getItem(StoreService.STORE_ID);
        localStorage.setItem(rangeKey, JSON.stringify(selectedRange));

        EventBus.$emit(EventBus.UPDATE_SELECTED_RANGE);
    }

    private isZoneEnabled(zoneCode: string) {
        const zone = this.currentZones.find(zone => zone.zoneCode === zoneCode);

        return zone && zone.enabled === true;
    }

    public refreshStore() {
        this.getStoreById().then(store => {
            this.store = store;
            EventBus.$emit(EventBus.REFRESH_STORE, this.store.zones);
        })
    }
    
    public getStores(): Promise<GetStoresResponse> {
        return storesApiClient.get(StoreService.STORES_ENDPOINT + "/all")
            .then((response: any) => {
                const data: GetStoresResponse = response && response.data ? response.data : {};
                return Promise.resolve(data);
            }).catch(error => {
                return Promise.reject(error);
            })
    }

    public getStoreById(): Promise<Store> {
        const storeId = sessionStorage.getItem(StoreService.STORE_ID);

        return storesApiClient.get(StoreService.STORES_ENDPOINT + "/" + storeId)
            .then((response: any) => {
                const data: Store = response && response.data ? response.data : {};
                return Promise.resolve(data);
            }).catch(error => {
                return Promise.reject(error);
            })
    }

    public updateClosures(store: Store, zone: Zone): Promise<Store> {
        return storesApiClient.patch(StoreService.STORES_ENDPOINT + "/" + store.storeId + "/" + zone.zoneCode, zone.closures)
            .then((response: any) => {
                const data: Store = response && response.data ? response.data : {};
                return Promise.resolve(data);
            }).catch(error => {
                return Promise.reject(error);
            })
    }

    public updateStoreInSession(store: Store, pageName?: PageName) {
        sessionStorage.setItem('storeId', store.storeId);
        sessionStorage.setItem('storeLabel', store.storeLabel);
        EventBus.$emit(EventBus.REFRESH_STORE_ID, pageName);
    }
}